<template>
  <transition name="modal">
    <div class="modal-container">
      <div class="modal">
        <div class="modal-dialog" ref="refModalDialog" :style="{'max-width': maxWidthStr}">
          <div class="modal-content">
            <div class="modal-header" v-if="showHeader" ref="refModalHeader">
              <button
                v-if="showTopDismissButton"
                :class="topDismissButtonClass"
                @click="$emit('dismiss')"
              >
                {{topDismissButtonLabel}}
              </button>
              <slot name="header">
                <h2 class="title">{{title}}</h2>
              </slot>
            </div>
            <div class="modal-body" :style="{'max-height': state.modalBodyMaxHeight+'px'}">
              <slot name="body" :modalBodyMaxHeight="state.modalBodyMaxHeight">{{body}}</slot>
            </div>
            <div class="modal-footer" v-if="showFooter" ref="refModalFooter">
              <slot name="footer">
                <template v-if="flipBottomButton">
                  <button
                    class="btn btn-secondary"
                    @click="$emit('dismiss')"
                    v-if="showBottomDismissButton"
                  >
                    {{bottomDismissButtonLabel}}
                  </button>
                </template>
                <button
                  class="btn"
                  :class="bottomCloseButtonClass"
                  :disabled="shouldCloseButtonDisabled"
                  @click="$emit('close')"
                  v-if="showBottomCloseButton"
                >
                  <i class="fa fa-spinner fa-spin" v-show="isCloseButtonBusy"></i>
                  <i
                    class="fa"
                    v-if="showBottomCloseButtonIcon"
                    :class="bottomCloseButtonIconClass"
                    v-show="!isCloseButtonBusy"
                  ></i>
                  <span v-show="!isCloseButtonBusy">
                    {{bottomCloseButtonLabel}}
                  </span>
                </button>
                <template v-if="!flipBottomButton">
                  <button
                    class="btn btn-secondary"
                    @click="$emit('dismiss')"
                    v-if="showBottomDismissButton"
                  >
                    {{bottomDismissButtonLabel}}
                  </button>
                </template>
              </slot>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop">
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import {
  onMounted,
  useTemplateRef,
  reactive,
  computed,
} from 'vue';

interface ModalState{
  modalBodyMaxHeight: number;
}

// 汎用モーダル。呼び出し元ごとに使う項目が異なるが、全propにwithDefaultsで既定値を設定している
interface Props {
  title?: string;
  body?: string;
  showHeader?: boolean;
  showTopDismissButton?: boolean;
  topDismissButtonLabel?: string;
  topDismissButtonClass?: string;
  isCloseButtonBusy?: boolean;
  isCloseButtonDisabled?: boolean;
  showFooter?: boolean;
  showBottomCloseButton?: boolean;
  bottomCloseButtonLabel?: string;
  bottomCloseButtonClass?: string;
  showBottomCloseButtonIcon?: boolean;
  bottomCloseButtonIconClass?: string;
  showBottomDismissButton?: boolean;
  bottomDismissButtonLabel?: string;
  flipBottomButton?: boolean;
  maxWidthStr?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  body: '',
  showHeader: true,
  showTopDismissButton: false,
  topDismissButtonLabel: 'x',
  topDismissButtonClass: 'close',
  isCloseButtonBusy: false,
  isCloseButtonDisabled: false,
  showFooter: true,
  showBottomCloseButton: true,
  bottomCloseButtonLabel: 'OK',
  bottomCloseButtonClass: 'btn-primary',
  showBottomCloseButtonIcon: false,
  bottomCloseButtonIconClass: 'fa-trash',
  showBottomDismissButton: true,
  bottomDismissButtonLabel: 'キャンセル',
  flipBottomButton: false,
});

defineEmits<{
  (e: 'dismiss'): void;
  (e: 'close'): void;
}>();

const refModalDialog = useTemplateRef<HTMLElement>('refModalDialog');
const refModalHeader = useTemplateRef<HTMLElement>('refModalHeader');
const refModalFooter = useTemplateRef<HTMLElement>('refModalFooter');
const state = reactive<ModalState>({
  modalBodyMaxHeight: 350,
});
onMounted(() => {
  const windowH = window.innerHeight;
  const dialogOffsetTop = refModalDialog.value?.offsetTop || 0;
  const modalHeaderH = refModalHeader.value?.clientHeight || 0;
  const modalFooterH = refModalFooter.value?.clientHeight || 0;
  state.modalBodyMaxHeight = windowH - modalHeaderH - modalFooterH - dialogOffsetTop * 2;
});

const shouldCloseButtonDisabled = computed(() => {
  return props.isCloseButtonBusy || props.isCloseButtonDisabled;
});
</script>

<style lang="scss" scoped>
  .modal-container {
    font-size: 18px;
  }
  .modal {
    display: block;
  }
  .modal-dialog {
    min-width: 45%;
    height: 92%;
    max-height: 92%;
    .p1 {
      margin: 0 0 4px 0;
    }
    .p2 {
      margin: 20px 0 8px;
    }
  }
  @media only screen and (min-width: 1280px) {
    .modal-dialog {
      max-width: 600px;
      min-width: 600px;
    }
  }
  .modal-header {
    padding: 6px 10px;
    .title {
      margin: 6px 0;
    }
  }
  .modal-body {
    overflow-y: scroll;
  }
  .modal-footer {
    display: block;
    padding: 0.8rem 1rem;
    text-align: right;
  }

  .modal-backdrop {
    opacity: 0.5;
  }

  .modal-enter {
    opacity: 0;
  }
  .modal-enter-active {
    transition: opacity .0s ease;
  }
  .modal-enter-to {
    opacity: 1.0;
  }
  .modal-leave-active {
    opacity: 0;
  }
</style>
